/**
 * Types for the EditTestamentaryEntitySplitScreenModal
 *
 * This modal allows editing testamentary entities (sub-trusts) in a split-screen
 * layout with document viewing, similar to EditEntitySplitScreenModal.
 */

import Decimal from 'decimal.js';

import { TestamentaryEntityKind } from '@/types/schema';
import { PathsOf } from '@/types/subform';

import {
  BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE,
  BENEFICIARIES_SUBFORM,
} from '../../EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { EntityBeneficiariesFormBeneficiary } from '../../EntityBeneficiariesSubform/EntityBeneficiariesSubform.types';
import {
  EstateInclusionStatus,
  GSTStatus,
} from '../../TaxStatusSubform/TaxStatusSubform.types';
import {
  TRUST_DETAILS_SUBFORM_NAMESPACE,
  TrustDetailsSubformType_TrusteesAndSuccessorTrustees,
  TrustDetailsSubformType_TrusteesSuccessorTrusteesAndTrustAdvisors,
} from '../../TrustDetailsSubform/TrustDetailsSubform.types';
import {
  CONTROLLING_PARTY_SUBFORM,
  TESTAMENTARY_ENTITY,
} from './EditTestamentaryEntitySplitScreen.constants';

// ============================================================================
// Form Types
// ============================================================================

export interface ControllingPartyInputShape {
  controllingPartyId: string;
}

export const defaultControllingParty: ControllingPartyInputShape = {
  controllingPartyId: '',
};

export interface DefaultEstateTaxStatus {
  estateInclusionStatus?: EstateInclusionStatus;
  survivingSpouseStateInEstateStatus?: EstateInclusionStatus;
  gstStatus?: GSTStatus;
  inclusionRatio?: Decimal;
}

export interface EditTestamentaryEntityFormShape {
  [TESTAMENTARY_ENTITY]: {
    displayName: string;
    householdId: string;
    testamentaryEntityKind: TestamentaryEntityKind | '';
    entityDetailsNotes: string;
    estateInclusionStatus: EstateInclusionStatus | null;
    gstStatus: GSTStatus | '' | null;
    inclusionRatio: Decimal | null;
    survivingSpouseStateInEstateStatus: EstateInclusionStatus | null;
    // IDs of entities this testamentary entity was created from
    createdFromEntityIds: string[];
    // Account owner for inherited retirement account testamentary entities
    accountOwnerId: string;
  };
  [CONTROLLING_PARTY_SUBFORM]?: {
    controllingParties: ControllingPartyInputShape[];
  };
  [TRUST_DETAILS_SUBFORM_NAMESPACE]:
    | TrustDetailsSubformType_TrusteesAndSuccessorTrustees
    | TrustDetailsSubformType_TrusteesSuccessorTrusteesAndTrustAdvisors;
  [BENEFICIARIES_SUBFORM]: {
    beneficiaries: EntityBeneficiariesFormBeneficiary[];
  };
  [BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE]?: {
    beneficiaries: EntityBeneficiariesFormBeneficiary[];
  };
}

export type EditTestamentaryEntityFormField =
  PathsOf<EditTestamentaryEntityFormShape>;

// ============================================================================
// Section Types
// ============================================================================

export enum EditTestamentaryEntitySection {
  BASIC_INFORMATION = 'BASIC_INFORMATION',
  ACCESS_AFTER_DEATH = 'ACCESS_AFTER_DEATH',
  ACCESS_AFTER_BOTH_DEATHS = 'ACCESS_AFTER_BOTH_DEATHS',
  TRUSTEES = 'TRUSTEES',
  DISPOSITIONS = 'DISPOSITIONS',
}

export function isEditTestamentaryEntitySection(
  value: string | null | undefined
): value is EditTestamentaryEntitySection {
  return Object.values(EditTestamentaryEntitySection).includes(
    value as EditTestamentaryEntitySection
  );
}

export interface EditTestamentaryEntitySectionOption {
  label: string;
  section: EditTestamentaryEntitySection;
}

/**
 * Returns the section options for the testamentary entity modal.
 * The sections available depend on whether it's a two-client household.
 *
 * @param isTwoClientHousehold - Whether the household has two primary clients
 */
export function getTestamentaryEntitySectionOptions(
  isTwoClientHousehold: boolean
): EditTestamentaryEntitySectionOption[] {
  const sections: EditTestamentaryEntitySectionOption[] = [
    {
      label: 'Basic information',
      section: EditTestamentaryEntitySection.BASIC_INFORMATION,
    },
    {
      label: 'Trustees',
      section: EditTestamentaryEntitySection.TRUSTEES,
    },
    {
      label: isTwoClientHousehold
        ? 'Access after first death'
        : 'Access after death',
      section: EditTestamentaryEntitySection.ACCESS_AFTER_DEATH,
    },
  ];

  // Show "Access after both deaths" section for two-client households
  if (isTwoClientHousehold) {
    sections.push({
      label: 'Access after both deaths',
      section: EditTestamentaryEntitySection.ACCESS_AFTER_BOTH_DEATHS,
    });
  }

  // Only show Dispositions for two-client households.
  // Single-grantor testamentary entities don't have dispositive provisions.
  if (isTwoClientHousehold) {
    sections.push({
      label: 'Dispositions',
      section: EditTestamentaryEntitySection.DISPOSITIONS,
    });
  }

  return sections;
}

/**
 * Gets the next section in the list, or undefined if at the last section.
 */
export function getNextTestamentaryEntitySection(
  currentSection: EditTestamentaryEntitySection,
  sectionOptions: EditTestamentaryEntitySectionOption[]
): EditTestamentaryEntitySection | undefined {
  const currentIndex = sectionOptions.findIndex(
    (option) => option.section === currentSection
  );

  if (currentIndex === -1 || currentIndex === sectionOptions.length - 1) {
    return undefined;
  }

  return sectionOptions[currentIndex + 1]?.section;
}

/**
 * Gets the previous section in the list, or undefined if at the first section.
 */
export function getPreviousTestamentaryEntitySection(
  currentSection: EditTestamentaryEntitySection,
  sectionOptions: EditTestamentaryEntitySectionOption[]
): EditTestamentaryEntitySection | undefined {
  const currentIndex = sectionOptions.findIndex(
    (option) => option.section === currentSection
  );

  if (currentIndex <= 0) {
    return undefined;
  }

  return sectionOptions[currentIndex - 1]?.section;
}
